import * as React from "react"

import { Button } from "@/components/ui/button"
import { useT } from "@/i18n/provider"
import { adminContactUrl } from "@/lib/api"

/** Shown when a child throws during render — a friendly screen with a way to reach the
 *  admin, instead of a blank page. Localized via the provider mounted above it. */
function ErrorFallback({ onReset }: { onReset: () => void }) {
  const t = useT()
  const url = adminContactUrl()
  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t("common.errorTitle")}</h1>
        <p className="text-muted-foreground max-w-md text-sm">{t("common.errorBody")}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button onClick={onReset}>{t("common.errorReload")}</Button>
        <Button
          variant="outline"
          onClick={() =>
            url ? window.open(url, "_blank", "noopener") : window.location.assign("/billing")
          }
        >
          {t("common.contactAdmin")}
        </Button>
      </div>
    </div>
  )
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: unknown): void {
    // Surface it for debugging; the fallback keeps the user unstuck.
    console.error("Unhandled UI error:", error)
  }

  private handleReset = () => {
    // A full reload is the most reliable recovery from a corrupted render tree.
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={this.handleReset} />
    }
    return this.props.children
  }
}
